import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Heart, User, ClipboardList } from 'lucide-react';

/**
 * MobileBottomNav — cinematic glassmorphic bottom bar used across the
 * customer-facing experience on mobile. Matches the homepage palette
 * (#05070d / amber #F2A900) so the brand feels seamless top-to-bottom.
 */
const MobileBottomNav = ({ isAuthenticated, userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY || currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Don't show on dealer/admin/auth pages or standalone intake form.
  // Also hide inside the customer matches dashboard since that view
  // already has its own dedicated sidebar nav.
  const hiddenPaths = ['/dealer', '/admin', '/intake-form', '/trade-appraisal', '/dealer-auth', '/auth', '/login', '/forgot-password', '/reset-password', '/my-matches', '/embed', '/rigready'];
  const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path));
  if (shouldHide) return null;

  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/',
      active: location.pathname === '/',
    },
    {
      id: 'quiz',
      label: 'Match Form',
      icon: ClipboardList,
      path: '/#start-application',
      active: false,
    },
    {
      id: 'matches',
      label: 'My Matches',
      icon: Search,
      path: '/sign-in',
      active: location.pathname === '/sign-in' || location.pathname === '/my-quiz',
    },
    {
      id: 'favorites',
      label: 'Favorites',
      icon: Heart,
      path: '/favorites',
      active: location.pathname === '/favorites',
    },
    {
      id: 'profile',
      label: isAuthenticated ? 'Dashboard' : 'Sign In',
      icon: User,
      path: isAuthenticated
        ? (userRole === 'dealer' || userRole === 'super_admin' ? '/dealer' : '/sign-in')
        : '/sign-in',
      active: location.pathname === '/sign-in',
    },
  ];

  const handleClick = (path) => {
    if (path.startsWith('/#')) {
      // Hash navigation on the homepage — scroll to the target section
      if (location.pathname === '/') {
        const id = path.split('#')[1];
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      }
      navigate(path);
      return;
    }
    navigate(path);
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-nav"
    >
      {/* Subtle top fade so the bar doesn't fight content above it */}
      <div className="absolute -top-6 inset-x-0 h-6 bg-gradient-to-t from-[#05070d]/90 to-transparent pointer-events-none" />

      {/* Glassmorphic bar */}
      <div className="relative bg-[#05070d]/85 backdrop-blur-xl border-t border-white/10 shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.6)]">
        <div className="flex items-stretch justify-around h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item.path)}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors duration-200 ${
                  item.active
                    ? 'text-[#F2A900]'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`mobile-nav-${item.id}`}
              >
                {/* Active glow */}
                {item.active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[3px] rounded-b-full bg-[#F2A900] shadow-[0_0_18px_2px_rgba(242,169,0,0.55)]" />
                )}
                <Icon
                  size={22}
                  className={item.active ? 'stroke-[2.4px]' : 'stroke-[1.8px]'}
                />
                <span className={`text-[10px] tracking-wide ${item.active ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
